import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Divider, Chip
} from '@mui/material';
import {
  Dashboard, Inventory, ShoppingCart, RequestQuote, Chat, Build,
  Menu as MenuIcon, Logout, Home
} from '@mui/icons-material';
import LanguageSwitcher from '../LanguageSwitcher';

const drawerWidth = 260;
const menuItems = [
  { text: 'supplier_dashboard', icon: <Dashboard />, path: '/supplier' },
  { text: 'my_products', icon: <Inventory />, path: '/supplier/products' },
  { text: 'incoming_orders', icon: <ShoppingCart />, path: '/supplier/orders' },
  { text: 'quotes', icon: <RequestQuote />, path: '/supplier/quotes' },
  { text: 'Customization Requests', icon: <Build />, path: '/supplier/customization-requests' },
  { text: 'messages', icon: <Chat />, path: '/supplier/chat' },
];

const SupplierLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="primary">OSESG</Typography>
        <Chip label="Supplier Portal" color="primary" size="small" sx={{ mt: 0.5 }} />
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button key={item.path} component={Link} to={item.path}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2, mb: 0.5,
              '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
              '&:hover': { bgcolor: 'primary.light', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={t(item.text)} primaryTypographyProps={{ fontSize: '0.9rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        <ListItem button onClick={() => navigate('/')}>
          <ListItemIcon sx={{ minWidth: 40 }}><Home /></ListItemIcon>
          <ListItemText primary="Main Site" />
        </ListItem>
        <ListItem button onClick={() => { dispatch(logout()); navigate('/login'); }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary={t('logout')} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    // Force LTR for the dashboard chrome (sidebar/appbar/content regions):
    // the sidebar is a fixed-position Drawer anchored at the physical left,
    // but its flex placeholder Box would flip sides under document dir=rtl,
    // making the main content Box overlap and render underneath the sidebar.
    // Text content is still translated/RTL via i18n and per-element dir="rtl".
    <Box sx={{ display: 'flex', direction: 'ltr' }}>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>{t('supplier_dashboard')}</Typography>
          <LanguageSwitcher color="inherit" />
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, mt: 8, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default SupplierLayout;
